import {
  Component,
  Input,
  SimpleChanges,
  OnChanges,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Chart } from 'angular-highcharts';
import { DateUtils } from 'src/app/shared/utils/date.utils';

@Component({
  selector: 'app-micro-chart',
  templateUrl: './micro-chart.component.html',
  styleUrls: ['./micro-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MicroChartComponent implements OnChanges, OnDestroy {
  /** Pre-computed chart series: [timestamp, count][] */
  @Input() seriesData: [number, number][];
  @Input() timeframe: string;
  /**
   * Groups the series into buckets of N days (7 = weekly). A 97-day window
   * drawn daily is ~97 hairlines per row; bucketing keeps the shape readable
   * at a fraction of the marks. Omit or 1 for raw daily points.
   */
  @Input() bucketDays?: number;

  stockChart: Chart;

  private chartOptions: Highcharts.Options = {
    title: { text: undefined },
    chart: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      type: 'area',
      margin: [2, 0, 2, 0],
      width: 140,
      height: 25,
      style: {
        overflow: 'visible',
      },
    },
    xAxis: {
      labels: { enabled: false },
      title: { text: null },
      lineWidth: 1,
      lineColor: 'rgb(50, 50, 50)',
      gridLineWidth: 0,
      gridLineColor: 'transparent',
      startOnTick: false,
      endOnTick: false,
      tickPositions: [],
    },
    yAxis: {
      endOnTick: false,
      startOnTick: false,
      labels: { enabled: false },
      title: { text: null },
      gridLineWidth: 0,
      gridLineColor: 'transparent',
      tickPositions: [],
    },
    exporting: { enabled: false },
    credits: { enabled: false },
    legend: { enabled: false },
    tooltip: { enabled: false },
    plotOptions: {
      series: {
        animation: false,
        shadow: false,
      },
      column: {
        pointWidth: 5,
        grouping: false,
        borderColor: 'transparent',
      },
    },
    series: [
      {
        name: '',
        type: 'area',
        data: [],
      } as Highcharts.SeriesOptionsType,
    ],
  };

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.seriesData && !changes.timeframe && !changes.bucketDays) {
      return;
    }

    const range = this.getRange();
    const data = this.bucketSeries(range);
    this.chartOptions.series = [
      {
        type: 'column',
        color: '#1e816c',
        data,
      } as Highcharts.SeriesOptionsType,
    ];
    this.chartOptions.plotOptions = {
      ...this.chartOptions.plotOptions,
      column: {
        ...this.chartOptions.plotOptions?.column,
        pointWidth: this.pointWidthFor(range),
      },
    };
    this.chartOptions.xAxis = {
      ...(this.chartOptions.xAxis as Highcharts.XAxisOptions),
      type: 'datetime',
      ordinal: false,
      min: range.min,
      max: range.max,
    };
    this.stockChart = new Chart(this.chartOptions);
  }

  ngOnDestroy(): void {
    this.stockChart?.destroy();
  }

  private getRange(): { min: number; max: number } {
    const fromto = DateUtils.chartFromTo(this.timeframe);
    return {
      min: new Date(fromto.dateFrom + 'T00:00:00Z').getTime(),
      max: new Date(fromto.dateTo + 'T00:00:00Z').getTime(),
    };
  }

  /**
   * Sums counts into `bucketDays` blocks anchored to the end of the range, so
   * the newest bucket always closes on `range.max` and the bars don't drift as
   * the window slides. Each bucket is plotted at its midpoint.
   */
  private bucketSeries(range: {
    min: number;
    max: number;
  }): [number, number][] | null {
    const size = this.bucketDays ?? 1;
    if (!this.seriesData?.length || size <= 1) {
      return this.seriesData || null;
    }
    const span = size * 86400000;
    const totals = new Map<number, number>();
    for (const [timestamp, count] of this.seriesData) {
      const index = Math.floor((range.max - timestamp) / span);
      const end = range.max - index * span;
      totals.set(end - span / 2, (totals.get(end - span / 2) ?? 0) + count);
    }
    return [...totals.entries()].sort((a, b) => a[0] - b[0]);
  }

  private pointWidthFor(range: { min: number; max: number }): number {
    const days = Math.max(1, Math.round((range.max - range.min) / 86400000));
    const size = this.bucketDays ?? 1;
    const bucketed = size > 1;
    const buckets = Math.max(1, Math.ceil(days / size));
    const chartWidth = 140;
    const slot = Math.floor(chartWidth / buckets);
    // Bucketed bars may go wider than the legacy 5px daily column, less a
    // hairline gap so grouped weeks stay legible.
    return bucketed
      ? Math.max(1, Math.min(10, slot - 2))
      : Math.max(1, Math.min(5, slot));
  }
}
